'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '@/components/data-table/data-table';
import { DataTableSearch } from '@/components/data-table/data-table-search';
import { DataTableSortList } from '@/components/data-table/data-table-sort-list';
import { useDataTable } from '@/hooks/use-data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';
import type { TableConfig } from '@/types/data-table';
import type { MainTableState } from '@/lib/utils/table-state-utils';
import type { ReportLibraryRow } from '@/app/reports/types/report-library-row';
import { getReportsTableColumns } from '@/app/reports/components/reports-table-columns';
import { ReportsTableActionBar } from '@/app/reports/components/reports-table-action-bar';
import { ReportsTableSkeleton } from '@/app/reports/components/ReportsTableSkeleton';

type ReportsTableProps = {
  rows: ReportLibraryRow[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onTableStateChange: (state: MainTableState) => void;
  onTogglePin: (reportId: string) => void;
  onRefetch: () => void;
  reportDetailsHref: (reportId: string) => string;
  config?: TableConfig;
};

export function ReportsTable({
  rows,
  loading,
  searchQuery,
  onSearchChange,
  onTableStateChange,
  onTogglePin,
  onRefetch,
  reportDetailsHref,
  config = {},
}: ReportsTableProps) {
  const { t, i18n } = useTranslation('reports');
  const navigate = useNavigate();

  const columns = React.useMemo(
    () =>
      getReportsTableColumns({
        t,
        reportDetailsHref,
        onTogglePin,
      }),
    [t, reportDetailsHref, onTogglePin, i18n.language],
  );

  const { table } = useDataTable({
    data: rows,
    columns,
    total: rows.length,
    config: {
      ...config,
      clientMode: true,
      defaultSorting: [
        { id: 'isPinned', desc: true },
        { id: 'lastViewedAt', desc: true },
      ],
    },
    onTableStateChange,
    getRowId: (row: ReportLibraryRow) => row.id,
  });

  const handleRowClick = React.useCallback(
    (row: ReportLibraryRow) => {
      navigate(reportDetailsHref(row.id));
    },
    [navigate, reportDetailsHref],
  );

  return (
    <DataTable
      key={i18n.language}
      table={table}
      actionBar={<ReportsTableActionBar table={table} />}
      isLoading={loading}
      skeletonRows={<ReportsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onRowClick={handleRowClick}
      extraFilters={
        <div className="w-full md:w-[220px]">
          <DataTableSearch
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={t('library.searchPlaceholder')}
          />
        </div>
      }
    >
      <DataTableSortList table={table} align="end" />
    </DataTable>
  );
}
